import { useMemo } from 'react';
import type { useThemeContext } from '@/context/theme';
import { useSidebarActive } from '@/features/sidebar';
import type { useCopilotSidebar } from '@/features/copilot';
import type { useSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import { close as closeSidebar } from '@/features/sidebar';
import type { LocalCommand } from './local-commands';
import { matchesLocalCommand } from './utils/search-commands';

type ColorScheme = ReturnType<typeof useThemeContext>['colorScheme'];
type SetColorScheme = ReturnType<typeof useThemeContext>['setColorScheme'];
type SideNavState = ReturnType<typeof useSideNavState>;
type ToggleSideNav = ReturnType<typeof useToggleSideNav>;

type SettingsCommandsDeps = {
  colorScheme: ColorScheme;
  setColorScheme: SetColorScheme;
  sideNavState: SideNavState;
  toggleSideNav: ToggleSideNav;
  openCopilot: ReturnType<typeof useCopilotSidebar>['openCopilot'];
};

type SettingsCommandsOptions = {
  includeEmptyQuery?: boolean;
};

function themeCommands(colorScheme: ColorScheme, setColorScheme: SetColorScheme): LocalCommand[] {
  const cmds: LocalCommand[] = [];
  if (colorScheme !== 'dark') {
    cmds.push({
      id: 'settings:theme-dark',
      label: 'Switch to dark mode',
      description: 'Use a dark color scheme across the app',
      icon: 'dark-mode',
      keywords: ['theme', 'dark', 'night', 'appearance'],
      onSelect: () => setColorScheme('dark'),
    });
  }
  if (colorScheme !== 'light') {
    cmds.push({
      id: 'settings:theme-light',
      label: 'Switch to light mode',
      description: 'Use a light color scheme across the app',
      icon: 'light-mode',
      keywords: ['theme', 'light', 'day', 'appearance'],
      onSelect: () => setColorScheme('light'),
    });
  }
  if (colorScheme !== 'auto') {
    cmds.push({
      id: 'settings:theme-system',
      label: 'Match system theme',
      description: 'Follow your operating system light or dark setting',
      icon: 'desktop',
      keywords: ['theme', 'system', 'auto', 'appearance'],
      onSelect: () => setColorScheme('auto'),
    });
  }
  return cmds;
}

function sidebarCommand(sideNavState: SideNavState, toggleSideNav: ToggleSideNav): LocalCommand | null {
  if (sideNavState == null || toggleSideNav == null) return null;
  return sideNavState.isCollapsed
    ? {
        id: 'settings:expand-sidebar',
        label: 'Expand sidebar',
        description: 'Show the main navigation at full width',
        icon: 'expand',
        keywords: ['sidebar', 'nav', 'navigation', 'expand', 'show'],
        onSelect: () => toggleSideNav(),
      }
    : {
        id: 'settings:collapse-sidebar',
        label: 'Collapse sidebar',
        description: 'Collapse the main navigation to icons only',
        icon: 'collapse',
        keywords: ['sidebar', 'nav', 'navigation', 'collapse', 'hide'],
        onSelect: () => toggleSideNav(),
      };
}

function agentCommand(
  isAiEnabled: boolean,
  isCopilotOpen: boolean,
  openCopilot: SettingsCommandsDeps['openCopilot'],
): LocalCommand | null {
  if (!isAiEnabled) return null;
  return isCopilotOpen
    ? {
        id: 'settings:hide-agent',
        label: 'Hide the Agent',
        description: 'Close the Agent sidebar',
        icon: 'sparkle',
        keywords: ['agent', 'ai', 'copilot', 'assistant', 'sidebar', 'hide', 'close'],
        onSelect: () => {
          void closeSidebar();
        },
      }
    : {
        id: 'settings:show-agent',
        label: 'Show the Agent',
        description: 'Open the Agent sidebar to ask a question or run a workflow',
        icon: 'sparkle',
        keywords: ['agent', 'ai', 'copilot', 'assistant', 'sidebar', 'show', 'open'],
        onSelect: () => openCopilot({ triggerSource: 'command_dialog' }),
      };
}

// Context-dependent values are passed as deps rather than called as hooks directly because
// CommandDialog renders outside the provider tree. See useCommandDialog in use-command-dialog-state.ts.
export function useSettingsCommands(
  query: string,
  deps: SettingsCommandsDeps,
  options: SettingsCommandsOptions = {},
): LocalCommand[] {
  const { colorScheme, setColorScheme, sideNavState, toggleSideNav, openCopilot } = deps;
  const { includeEmptyQuery = false } = options;

  const isCopilotOpen = useSidebarActive('copilot');
  const isAiEnabled = useAccountSetting(AccountSetting.AiEnabled);

  return useMemo(() => {
    if (!includeEmptyQuery && query.trim().length === 0) return [];

    const sidebar = sidebarCommand(sideNavState, toggleSideNav);
    const agent = agentCommand(isAiEnabled === true, isCopilotOpen, openCopilot);

    return [
      ...themeCommands(colorScheme, setColorScheme),
      ...(sidebar != null ? [sidebar] : []),
      ...(agent != null ? [agent] : []),
    ].filter(matchesLocalCommand(query));
  }, [
    colorScheme,
    setColorScheme,
    sideNavState,
    toggleSideNav,
    isCopilotOpen,
    openCopilot,
    isAiEnabled,
    query,
    includeEmptyQuery,
  ]);
}
